import * as React from 'react';
import { useIntl, IntlShape } from 'react-intl';
import { ZodInvalidStringIssue, ZodIssue, ZodIssueOptionalMessage } from 'zod';

type IssueIntl<IssueType extends ZodIssueOptionalMessage> = (issue: IssueType, intl: IntlShape) => string | React.ReactNode

type IssueMapperDeclaration = {
  [K in ZodIssueOptionalMessage['code']]: IssueIntl<Extract<ZodIssueOptionalMessage, { code: K }>>
}

const StringErrorCodeMessages = [
  'must_be_tls',
  // Zod Built-in messages for string validations
  'Invalid url',
]

type StringIssueMapperDeclaration = Record<typeof StringErrorCodeMessages[number], IssueIntl<ZodInvalidStringIssue>>

const DefaultStringIssueMapper: StringIssueMapperDeclaration = {
  must_be_tls: (issue, intl) => intl.formatMessage({
    description: 'Generic error message for a URL string that must be TLS but is missing the https protocol prefix',
    defaultMessage: 'Please enter a secure URL starting with https://',
  }),
  'Invalid url': (issue, intl) => intl.formatMessage({
    description: 'Generic error message for an invalid URL string',
    defaultMessage: 'Please enter a valid URL (like https://example.com)',
  }),
};

//TODO(nibeauli): create a way of selectively overriding these properties or having a partial issue mapper
// see: https://zodjs.netlify.app/guide/error-handling#zodissuecode
const DefaultIssueMapper: IssueMapperDeclaration = {
  too_big: (issue, intl) => intl.formatMessage({
    description: 'Generic error message for an input that is too large',
    defaultMessage: 'Must be at most {maximum} characters',
  }, {
    maximum: (typeof issue.maximum == 'bigint' ? issue.maximum.toString() : issue.maximum),
  }),
  too_small: (issue, intl) => intl.formatMessage({
    description: 'Generic error message for an input that is too small',
    defaultMessage: 'Must be at least {minimum} characters',
  }, {
    minimum: (typeof issue.minimum == 'bigint' ? issue.minimum.toString() : issue.minimum),
  }),
  invalid_arguments: (issue, intl) => intl.formatMessage({
    description: 'Generic error message for invalid arguments',
    defaultMessage: 'Invalid arguments for property: {property}',
  }, {
    property: issue.path.join('.'),
  }),
  invalid_date: (issue, intl) => intl.formatMessage({
    description: 'Generic error message for an invalid date',
    defaultMessage: 'Invalid date for property: {property}',
  }, {
    value: issue.path.join('.'),
  }),
  unrecognized_keys: (issue, intl) => intl.formatMessage({
    description: 'Generic error message providing unrecognized properties or keys',
    defaultMessage: 'Unrecognized keys for property: {property}',
  }, {
    property: issue.path.join('.'),
  }),
  invalid_enum_value: (issue, intl) => intl.formatMessage({
    description: 'Generic error message for a provided value not being one of the enumerated possibilities',
    defaultMessage: 'Invalid value for property: {property}',
  }, {
    property: issue.path.join('.'),
  }),
  invalid_literal: (issue, intl) => intl.formatMessage({
    description: 'Generic error message for a provided value not being one of the enumerated possibilities',
    defaultMessage: 'Invalid literal for property: {property}',
  }, {
    property: issue.path.join('.'),
  }),
  invalid_string: (issue, intl) => {
    if (issue.message && issue.message in DefaultStringIssueMapper) {
      return DefaultStringIssueMapper[issue.message](issue, intl);
    }
    return intl.formatMessage({
      defaultMessage: 'Invalid string for property: {property}',
    }, {
      property: issue.path.join('.'),
    });
  },
  not_multiple_of: (issue, intl) => intl.formatMessage({
    defaultMessage: 'Value must be a multiple of {divisor} for property: {property}',
  }, {
    property: issue.path.join('.'),
    divisor: issue.multipleOf.toString(),
  }),
  custom: (issue, intl) => intl.formatMessage({
    defaultMessage: 'Custom error: {message}',
  }, {
    message: issue.message,
  }),
  /*
   * The following are not implemented because they don't make sense in the context of user inputs via form and are
   * only applicable for developer errors in data formatting
   */
  invalid_return_type: () => "",
  invalid_type: () => "",
  invalid_union: () => "",
  invalid_union_discriminator: () => "",
  invalid_intersection_types: () => "",
  not_finite: () => "", // truly confused on how this is even detected... maybe just for float.INFINITY values?
};


// Erase the strictness of the issue type from the declaration for ease of calling w/o needing to write a switch for every possible
// value of ZodIssueOptionalMessage['code']
type IssueMapper<T extends IssueMapperDeclaration> = {
  [K in keyof T]: (issue: ZodIssueOptionalMessage, intl: IntlShape) => string | React.ReactNode
}

export function LocalizedGenericErrorCode({ issue }: { issue: ZodIssue }) {
  // this is safe because we will be dispatching based on the code so the potential contravariant from the function signature won't arise. 
  const mapper = DefaultIssueMapper as unknown as IssueMapper<IssueMapperDeclaration>;
  const intl = useIntl();

  if (issue.code in mapper) {
    return mapper[issue.code](issue, intl);
  }
  // This is a catch-all for all other error codes
  // TODO(nibeauli): this should fire a telemetry event so we know it has happened. This is all sorts of badness.
  return (
    <>
      {intl.formatMessage({ defaultMessage: "Unknown error code {code} | Issue: {message}" }, {
        code: issue.code,
        message: issue.message
      })}
    </>
  );
}
